import React, { useEffect, useState } from "react";
import { GlobalStyles } from "../../constants/styles";
import { formattedDateTime } from "../../util/date";
import useTranslation from "../../i18n/useTranslation";

const { colors } = GlobalStyles;

export default function PrintGameSheetsModal({ open, games, onClose, onConfirm }) {
    const { t, language } = useTranslation();
    const [selectedIds, setSelectedIds] = useState([]);

    useEffect(() => {
        if (!open) return;
        const allIds = (games || []).map((g) => g.id);
        setSelectedIds(allIds);
    }, [open, games]);

    if (!open) return null;

    const toggleGame = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const allSelected = games && selectedIds.length === games.length;

    const toggleSelectAll = () => {
        if (!games || !games.length) return;
        if (allSelected) {
            setSelectedIds([]);
        } else {
            setSelectedIds(games.map((g) => g.id));
        }
    };

    const handleConfirm = () => {
        const selectedEvents = (games || []).filter((g) => selectedIds.includes(g.id));
        onConfirm(selectedEvents);
    };

    return (
        <>
            <div className="modal fade show d-block" tabIndex="-1" role="dialog">
                <div className="modal-dialog modal-lg" role="document">
                    <div
                        className="modal-content"
                        style={{
                            backgroundColor: colors.shark,
                            color: colors.white,
                            borderRadius: 12,
                        }}
                    >
                        <div className="modal-header">
                            <h5 className="modal-title">{t("printGameSheetsModal.title")}</h5>
                            <button
                                type="button"
                                className="close"
                                onClick={onClose}
                                aria-label={t("printGameSheetsModal.close")}
                            >
                                <span aria-hidden="true" style={{ color: colors.white }}>
                                    &times;
                                </span>
                            </button>
                        </div>

                        <div className="modal-body">
                            {(!games || !games.length) && (
                                <p>{t("printGameSheetsModal.noGamesAvailable")}</p>
                            )}

                            {games && games.length > 0 && (
                                <>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <button
                                            type="button"
                                            className="btn btn-sm"
                                            style={{
                                                backgroundColor: colors.canary,
                                                color: colors.woodsmoke,
                                                fontWeight: 600,
                                            }}
                                            onClick={toggleSelectAll}
                                        >
                                            {allSelected
                                                ? t("printGameSheetsModal.unselectAll")
                                                : t("printGameSheetsModal.selectAll")}
                                        </button>
                                        <small style={{ color: colors.manatee }}>
                                            {t("printGameSheetsModal.selectedCount", {
                                                selected: selectedIds.length,
                                                total: games.length,
                                            })}
                                        </small>
                                    </div>

                                    <div
                                        style={{
                                            maxHeight: 400,
                                            overflowY: "auto",
                                            borderTop: `1px solid ${colors.manatee}`,
                                            borderBottom: `1px solid ${colors.manatee}`,
                                        }}
                                    >
                                        <table className="table table-sm mb-0">
                                            <thead>
                                            <tr style={{ color: colors.manatee }}>
                                                <th style={{ width: 40 }}></th>
                                                <th>{t("printGameSheetsModal.headers.dateTime")}</th>
                                                <th>{t("printGameSheetsModal.headers.field")}</th>
                                                <th>{t("printGameSheetsModal.headers.match")}</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {games.map((event) => {
                                                const game = event.game;
                                                const home =
                                                    game?.homeTeam?.name ||
                                                    game?.homeTeamAlias ||
                                                    t("printGameSheetsModal.fallbacks.home");
                                                const away =
                                                    game?.visitorTeam?.name ||
                                                    game?.visitorTeamAlias ||
                                                    t("printGameSheetsModal.fallbacks.away");

                                                const dt = formattedDateTime(
                                                    event.date,
                                                    event.timezone,
                                                    language || "en",
                                                    "LLL d, yyyy • h:mm a"
                                                );

                                                const fieldName =
                                                    event.field?.name ||
                                                    t("printGameSheetsModal.fallbacks.emptyValue");

                                                return (
                                                    <tr key={event.id}>
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedIds.includes(event.id)}
                                                                onChange={() => toggleGame(event.id)}
                                                            />
                                                        </td>
                                                        <td>{dt}</td>
                                                        <td>{fieldName}</td>
                                                        <td>
                                                            {away}{" "}
                                                            <span style={{ color: colors.manatee }}>
                                                                    {t("printGameSheetsModal.vs")}
                                                                </span>{" "}
                                                            {home}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onClose}
                            >
                                {t("printGameSheetsModal.close")}
                            </button>
                            <button
                                type="button"
                                className="btn"
                                style={{
                                    backgroundColor:
                                        selectedIds.length > 0
                                            ? colors.canary
                                            : colors.manatee,
                                    color: colors.woodsmoke,
                                    fontWeight: 600,
                                }}
                                disabled={selectedIds.length === 0}
                                onClick={handleConfirm}
                            >
                                {t("printGameSheetsModal.printGames", {
                                    count: selectedIds.length,
                                })}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal-backdrop fade show" onClick={onClose}></div>
        </>
    );
}